import React, { useEffect, useRef, useState } from "react";
import {
  Typography,
  Box,
  Button,
  Switch,
  Collapse,
  ListItem,
  List,
  ListItemText,
  ListItemIcon,
  Grid,
  TableCell,
  TableRow,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
  Avatar,
  Drawer,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { ChevronRight, ExpandLess, ExpandMore } from "@material-ui/icons";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import {
  fetchScreensAndFeatures,
  updateCollaboratorRoleChanges,
} from "../../../containers/actions/common/rolesAndAccessActions";
import {
  FETCH_SCREENS_AND_FEATURES_LOADING,
  UPDATE_COLLABORATOR_ROLE_CHANGES,
} from "../../../containers/actions/types";
import usePrevious from "../../helpers/previousProps";
import { convertToLocaleDateTime } from "../functionalComponents/FormatDateTimeToLocale";

const useStyles = makeStyles((theme) => ({
  root: {
    "& .MuiSwitch-switchBase.Mui-checked": {
      color: "#1890ff",
    },
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
      backgroundColor: "#1890ff",
    },
  },
  dialogContent: {
    textAlign: "center",
    padding: "24px 36px 24px 36px",
  },
  avatar: {
    color: "green",
    margin: "0 auto",
    fontSize: "54px",
  },
  dialogActions: {
    justifyContent: "center",
    marginTop: theme.spacing(2),
  },
  container: {
    display: "flex",
    flexDirection: "column",
    minHeight: "90%",
    marginTop: theme.spacing(2),
  },
  listContainer: {
    border: "1px solid #ccc",
    padding: 0,
  },
  screen: {
    padding: theme.spacing(1),
    backgroundColor: "#f5f5f5",
  },
  feature: {
    padding: theme.spacing(1),
    borderTop: "1px solid #ccc",
  },
  buttonContainer: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
}));

// const calculateDaysAgo = (date) => {
//   const today = new Date();

//   // Convert UTC date to IST
//   const givenDate = new Date(date);
//   const istOffset = 5 * 60 + 30; // IST offset in minutes
//   const istDate = new Date(givenDate.getTime() + istOffset * 60 * 1000);

//   // Difference calculation: compare today (local time) with given date (adjusted to IST)
//   const differenceInMilliseconds = today - istDate;
//   const differenceInDays = Math.floor(
//     differenceInMilliseconds / (1000 * 60 * 60 * 24)
//   );

//   if (differenceInDays === 0) {
//     const differenceInHours = Math.floor(
//       differenceInMilliseconds / (1000 * 60 * 60)
//     );
//     if (differenceInHours === 0) {
//       const differenceInMinutes = Math.floor(
//         differenceInMilliseconds / (1000 * 60)
//       );
//       if (differenceInMinutes <= 1) {
//         return "Now"; // Event occurred just now or less than 1 minute ago
//       }
//       return `${differenceInMinutes} minutes ago`;
//     }
//     return `${differenceInHours} hour${differenceInHours > 1 ? "s" : ""} ago`;
//   }
//   return `${differenceInDays} day${
//     differenceInDays > 1 ? "s" : ""
//   } ago at ${istDate.toLocaleTimeString("en-IN", {
//     hour: "2-digit",
//     minute: "2-digit",
//     hour12: true,
//   })}`;
// };

const calculateDaysAgo = (date) => {
  if (!date) return "Invalid Date";

  const today = new Date(); // Get current local time
  const localDate = new Date(convertToLocaleDateTime(date)); // Convert UTC to local time

  const differenceInMilliseconds = today - localDate;
  const differenceInDays = Math.floor(
    differenceInMilliseconds / (1000 * 60 * 60 * 24)
  );

  if (differenceInDays === 0) {
    const differenceInHours = Math.floor(
      differenceInMilliseconds / (1000 * 60 * 60)
    );
    if (differenceInHours === 0) {
      const differenceInMinutes = Math.floor(
        differenceInMilliseconds / (1000 * 60)
      );
      if (differenceInMinutes <= 1) {
        return "Now"; // Event occurred just now or less than 1 minute ago
      }
      return `${differenceInMinutes} minutes ago`;
    }
    return `${differenceInHours} hour${differenceInHours > 1 ? "s" : ""} ago`;
  }
  return `${differenceInDays} day${differenceInDays > 1 ? "s" : ""} ago`;
};


export default function Permissions({
  selectedRole,
  closeEditScreen,
  handleRefresh,
}) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [initialScreensAndFeatures, setInitialScreensAndFeatures] =
    useState(null); // To track original values
  const [saveDisabled, setSaveDisabled] = useState(true);
  const didMountRef = useRef(false);
  const [updateError, setUpdateError] = useState(false);
  const [updatedData, setUpdatedData] = useState({});
  const [openSuccessDialog, setOpenSuccessDialog] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [screensAndFeatures, setScreensAndFeatures] = useState({
    data: { screensAndFeatures: [] }, // Default structure
  });

  const ScreensAndFeaturesData = useSelector((state) => {
    return state?.pcmp?.common?.rolesAndAccess?.screensAndFeatureDetails
      ? state?.pcmp?.common?.rolesAndAccess?.screensAndFeatureDetails
      : [];
  });

  const ScreensAndFeaturesLoading = useSelector((state) => {
    return state?.pcmp?.common?.rolesAndAccess?.screensAndFeatureDetailsLoading
      ? state?.pcmp?.common?.rolesAndAccess?.screensAndFeatureDetailsLoading
      : [];
  });

  let errors = useSelector((state) =>
    state?.pcmp?.common?.rolesAndAccess?.screensAndFeatureDetails
      ? state?.pcmp?.common?.rolesAndAccess?.screensAndFeatureDetails
      : {
        status: "false",
        errorDescription:
          "Something went wrong. Please contact Schrocken Support.",
      }
  );

  const updateCollaboratorRoleData = useSelector(
    (state) => state?.pcmp?.common?.rolesAndAccess?.updateCollaboratorRole || {}
  );

  const prevUpdateCollaboratorRoleData = usePrevious(
    updateCollaboratorRoleData
  );

  useEffect(() => {
    if (selectedRole) {
      dispatch({
        type: FETCH_SCREENS_AND_FEATURES_LOADING,
        payload: true,
      });

      dispatch(
        fetchScreensAndFeatures({
          collaboratorRoleID: selectedRole?.collaborator_role_id,
          saas_user_id: "0",
        })
      );
    }
  }, [selectedRole]); // Add selectedRole as a dependency to trigger when it changes

  useEffect(() => {
    if (
      ScreensAndFeaturesData &&
      Array.isArray(ScreensAndFeaturesData?.data?.screensAndFeatures)
    ) {
      setScreensAndFeatures(ScreensAndFeaturesData);
      setInitialScreensAndFeatures(ScreensAndFeaturesData);
    }
  }, [ScreensAndFeaturesData]);

  const ScreensAndFeatures = screensAndFeatures?.data?.screensAndFeatures || [];

  const UserAvatar = ({ createdByImage, createdByName }) => {
    return (
      <Box display="flex" alignItems="center">
        <Avatar
          src={createdByImage} // Use the Base64 string here
          alt={createdByName} // Optional: Alt text for accessibility
          style={{ marginRight: "8px" }} // Adjust spacing if needed
        />
      </Box>
    );
  };

  const handleExpand = (screenId) => {
    setExpandedSections((prev) => ({
      ...prev,
      [screenId]: !prev[screenId],
    }));
  };

  const handleClose = () => {
    dispatch({
      type: UPDATE_COLLABORATOR_ROLE_CHANGES,
      value: {},
    });
    handleRefresh();
    setOpenSuccessDialog(false);
    closeEditScreen();
  };

  const handleToggleScreen = (screenId) => {
    setScreensAndFeatures((prev) => {
      return {
        ...prev,
        data: {
          ...prev.data,
          screensAndFeatures: prev.data.screensAndFeatures.map((screen) => {
            if (screen.screen_id === screenId) {
              // Compute the next state for screen_access
              const newScreenAccess = !screen.screen_access;

              // Update features based on the new screen_access
              return {
                ...screen,
                screen_access: newScreenAccess,
                features: screen.features.map((feature) => ({
                  ...feature,
                  feature_access: newScreenAccess
                    ? feature.feature_access
                    : false,
                })),
              };
            }
            return screen;
          }),
        },
      };
    });
  };

  const handleToggleFeature = (screenId, featureId) => {
    setScreensAndFeatures((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        screensAndFeatures: prev.data.screensAndFeatures.map((screen) =>
          screen.screen_id === screenId
            ? {
              ...screen,
              features: screen.features.map((feature) =>
                feature.feature_id === featureId
                  ? { ...feature, feature_access: !feature.feature_access }
                  : feature
              ),
            }
            : screen
        ),
      },
    }));
  };

  const handleDiscardChanges = () => {
    setScreensAndFeatures(initialScreensAndFeatures);
    // setRoleDetails(initialRoleDetails);
  };

  // const handleResetToDefault = () => {
  //   dispatch({
  //     type: FETCH_SCREENS_AND_FEATURES_LOADING,
  //     payload: true,
  //   });

  //   dispatch(
  //     fetchScreensAndFeatures({
  //       collaboratorRoleID: selectedRole?.collaborator_role_id,
  //     })
  //   );
  // };

  const handleResetToDefault = () => {
    setIsDrawerOpen(true); // Open the drawer
  };

  const handleConfirmReset = () => {
    setScreensAndFeatures((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        screensAndFeatures: prev.data.screensAndFeatures.map((screen) => ({
          ...screen,
          screen_access: true,
          features: screen.features.map((feature) => ({
            ...feature,
            feature_access: true,
          })),
        })),
      },
    }));

    // Fetch updated data
    // dispatch({
    //   type: FETCH_SCREENS_AND_FEATURES_LOADING,
    //   payload: true,
    // });

    // dispatch(
    //   fetchScreensAndFeatures({
    //     collaboratorRoleID: selectedRole?.collaborator_role_id,
    //   })
    // );

    setIsDrawerOpen(false); // Close the drawer after confirming
  };

  const handleSaveChanges = () => {
    setUpdateError(false);
    dispatch({
      type: UPDATE_COLLABORATOR_ROLE_CHANGES,
      payload: {},
    });
    const updatedScreensAndFeatures =
      screensAndFeatures.data.screensAndFeatures.reduce(
        (acc, screen, index) => {
          const initialScreen =
            initialScreensAndFeatures.data.screensAndFeatures[index];

          const isScreenAccessUpdated =
            screen.screen_access !== initialScreen.screen_access;

          // Build features array based on screen_access state
          const features =
            isScreenAccessUpdated && !screen.screen_access
              ? screen.features.map((feature) => ({
                feature_id: feature.feature_id,
                feature_access: false, // Disable all features if screen_access is disabled
              }))
              : screen.features
                .filter(
                  (feature, idx) =>
                    feature.feature_access !==
                    initialScreen.features[idx].feature_access
                )
                .map((feature) => ({
                  feature_id: feature.feature_id,
                  feature_access: feature.feature_access,
                }));

          // Add to the request object if screen_access or features are updated
          if (isScreenAccessUpdated || features.length > 0) {
            acc.push({
              screen_id: screen.screen_id,
              screen_access: screen.screen_access,
              features: features,
            });
          }

          return acc;
        },
        []
      );

    // Proceed only if there are updates
    // if (updatedScreensAndFeatures.length > 0) {
    const requestObject = {
      collaboratorRoleData: {
        collaboratorRoleDetails: {
          collaborator_role_id: selectedRole?.collaborator_role_id,
          role_name: null,
          role_description: null,
        },
        screensAndFeatures: updatedScreensAndFeatures
          ? updatedScreensAndFeatures
          : [],
      },
    };

    dispatch(updateCollaboratorRoleChanges(requestObject));
    // }
  };

  useEffect(() => {
    if (didMountRef.current) {
      if (
        JSON.stringify(updateCollaboratorRoleData) !==
        JSON.stringify(prevUpdateCollaboratorRoleData)
      ) {
        if (updateCollaboratorRoleData.status === true) {
          // setLoading(false);
          setUpdateError(false);
          setUpdatedData(updateCollaboratorRoleData);
          setOpenSuccessDialog(true);
        } else if (updateCollaboratorRoleData.status === false) {
          // setLoading(false);
          setUpdateError(true);
          scrollToTop();
          setUpdatedData(updateCollaboratorRoleData);
          setOpenSuccessDialog(false);
        }
      }
    } else {
      didMountRef.current = true;
    }
  }, [updateCollaboratorRoleData, prevUpdateCollaboratorRoleData]);

  const isSaveDisabled = () => {
    // Check for changes in screens and features
    const hasScreenOrFeatureChanges =
      screensAndFeatures.data.screensAndFeatures.some((screen, index) => {
        const initialScreen =
          initialScreensAndFeatures.data.screensAndFeatures[index];
        return (
          screen.screen_access !== initialScreen.screen_access ||
          screen.features.some(
            (feature, idx) =>
              feature.feature_access !==
              initialScreen.features[idx].feature_access
          )
        );
      });

    // Disable save button if no updates
    return !hasScreenOrFeatureChanges;
  };

  useEffect(() => {
    setSaveDisabled(isSaveDisabled());
  }, [screensAndFeatures]);

  const errRef = useRef();
  const scrollToTop = () => {
    setTimeout(
      () => errRef.current.scrollIntoView({ behavior: "smooth", block: "end" }),
      100
    );
  };

  return (
    <>
      <Box className={classes.container}>
        <List className={classes.listContainer} style={{ padding: "16px" }}>
          <Typography
            style={{
              fontSize: "1.3rem",
              fontWeight: "bold",
              paddingLeft: "2rem",
            }}
            gutterBottom
          >
            Permissions
          </Typography>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexDirection: "row",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.3rem",
                marginLeft: "2rem",
              }}
            >
              <Typography variant="body2" color="black">
                Last updated by
              </Typography>
              <UserAvatar
                createdByImage={selectedRole?.lastUpdatedByImage}
              // createdByName={selectedRole.lastUpdatedByName}
              />
              <Typography style={{ fontWeight: "600" }}>
                {selectedRole?.lastUpdatedByName || "NA"}
              </Typography>
              <Typography style={{ color: "#808080" }}>
                {calculateDaysAgo(selectedRole?.last_updated_date) || "NA"}
              </Typography>

              <Grid item>
                {/* <RandomAvatar name={hardcodedData.createdBy} /> */}
              </Grid>
            </div>
            <div
              style={{
                marginBottom: "0.3rem",
                display: "flex",
                alignItems: "center",
                flexDirection: "row",
                gap: "2rem",
              }}
            >
              <Grid>
                {" "}
                <Typography
                  color="secondary"
                  onClick={handleResetToDefault}
                  style={{
                    textDecoration: "underline",
                    cursor: "pointer",
                  }}
                >
                  Reset to default
                </Typography>
              </Grid>
              <Grid>
                {" "}
                <Button
                  onClick={handleDiscardChanges}
                  disabled={saveDisabled}
                  style={{ marginRight: "8px", backgroundColor: "#D3D3D3", textTransform: 'none' }}
                >
                  Discard Changes
                </Button>
              </Grid>
              <Grid>
                {" "}
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleSaveChanges}
                  disabled={saveDisabled}
                  style={{ cursor: "pointer", textTransform: 'none' }}
                >
                  Save Changes
                </Button>
              </Grid>
            </div>
          </div>

          {updateError === true && (
            <Grid
              ref={(el) => {
                errRef.current = el;
              }}
              style={{
                // paddingLeft: "150px",
                paddingTop: "8px",
                width: "93%",
                paddingBottom: "16px",
              }}
            >
              {/* {Object.keys(updatedData.errorDescription).map((key) => ( */}
              <Typography
                variant="h6"
                style={{
                  border: "1px solid #FF0000",
                  backgroundColor: "#FFF0F0",
                  opacity: "1",
                  textAlign: "center",
                  padding: "0.8rem",
                  marginLeft: "4rem",
                  fontSize: "0.875rem",
                }}
              >
                {updatedData?.errorDescription
                  ? updatedData.errorDescription
                  : updatedData.errorMessage}
              </Typography>
              {/* ))}  */}
            </Grid>
          )}

          {ScreensAndFeaturesLoading === true ? (
            <div style={{ textAlign: "center", padding: "2rem" }}>
              <CircularProgress color="primary" thickness={7} />
            </div>
          ) : ScreensAndFeatures.length > 0 ? (
            ScreensAndFeatures.map((screen) => (
              <React.Fragment key={screen.screen_id}>
                <ListItem
                  button
                  className={classes.screen}
                  onClick={() => handleExpand(screen.screen_id)}
                >
                  <ListItemIcon
                    style={{
                      visibility:
                        screen.features && screen.features.length > 0
                          ? "visible"
                          : "hidden",
                    }}
                  >
                    {expandedSections[screen.screen_id] ? (
                      <ExpandLess />
                    ) : (
                      <ExpandMore />
                    )}
                  </ListItemIcon>
                  <ListItemText>{screen.screen_name}</ListItemText>
                  <div style={{ marginRight: "40rem", textAlign: "center" }}>
                    <Switch
                      classes={{ root: classes.root }}
                      checked={screen.screen_access}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => handleToggleScreen(screen.screen_id)}
                    />
                  </div>
                </ListItem>
                {screen.features && screen.features.length > 0 && (
                  <Collapse
                    in={expandedSections[screen.screen_id]}
                    timeout="auto"
                    unmountOnExit
                  >
                    <List>
                      {screen.features.map((feature) => (
                        <ListItem
                          key={feature.feature_id}
                          button
                          className={classes.feature}
                        >
                          <ListItemText>{feature.feature}</ListItemText>
                          {feature?.last_updated_by ? (
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                                justifyContent: "space-between",
                                marginLeft: "16px",
                                marginRight: "14rem",
                                gap: "2rem",
                              }}
                            >
                              <Grid>
                                <Switch
                                  classes={{ root: classes.root }}
                                  checked={feature.feature_access}
                                  onChange={() =>
                                    handleToggleFeature(
                                      screen.screen_id,
                                      feature.feature_id
                                    )
                                  }
                                  disabled={!screen.screen_access} // Disable feature toggle if screen access is off
                                />
                              </Grid>

                              <Grid>
                                {feature?.last_updated_by && (
                                  <Typography
                                    variant="caption"
                                    style={{
                                      fontSize: "0.8rem",
                                      fontWeight: 550,
                                    }}
                                  >
                                    {feature?.feature_access
                                      ? "Enabled By"
                                      : "Disabled By"}{" "}
                                  </Typography>
                                )}
                              </Grid>

                              <Grid>
                                <UserAvatar
                                  createdByImage={
                                    selectedRole?.lastUpdatedByImage
                                  }
                                // createdByName={selectedRole.lastUpdatedByName}
                                />
                              </Grid>

                              <Grid>
                                <Typography
                                  variant="caption"
                                  style={{
                                    fontWeight: 600,
                                    width: "25rem",
                                    // backgroundColor: "yellow",
                                    fontSize: "0.8rem",
                                  }}
                                >
                                  {feature?.lastUpdatedByName}
                                </Typography>
                              </Grid>

                              <Grid>
                                <Typography
                                  variant="caption"
                                  style={{
                                    color: "#808080",
                                    fontSize: "0.8rem",
                                  }}
                                >
                                  {feature?.last_updated_date
                                    ? moment(feature.last_updated_date).format(
                                      "DD-MMM-YYYY"
                                    )
                                    : " "}
                                </Typography>
                              </Grid>
                            </div>
                          ) : (
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                                marginRight: "40rem",
                              }}
                            >
                              <Switch
                                classes={{ root: classes.root }}
                                checked={feature.feature_access}
                                onChange={() =>
                                  handleToggleFeature(
                                    screen.screen_id,
                                    feature.feature_id
                                  )
                                }
                                disabled={!screen.screen_access} // Disable feature toggle if screen access is off
                              />
                            </div>
                          )}
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                )}
              </React.Fragment>
            ))
          ) : errors.status === false ? (
            <div
              style={{
                border: "2px solid rgb(255,0,0)",
                backgroundColor: "rgb(255,240,240)",
                Opacity: 1,
                textAlign: "center",
                padding: "0.8rem",
                fontSize: "0.875rem",
                margin: "2rem",
              }}
            >
              {errors.errorDescription
                ? errors.errorDescription
                : "Please try again"}
            </div>
          ) : (
            <TableRow style={{ border: "2px solid rgba(224, 224, 224, 1)" }}>
              <TableCell colSpan={10} align="center">
                {"No Record Found"}
              </TableCell>
            </TableRow>
          )}
        </List>
      </Box>
    {updateCollaboratorRoleChanges.status === true && (
      <Dialog open={openSuccessDialog} maxWidth="sm" fullWidth>
        <DialogContent className={classes.dialogContent}>
          <CheckCircleIcon className={classes.avatar} />

          <Typography variant="h6" style={{ marginTop: "10px" }}>
            Details updated successfully
          </Typography>
          <Typography variant="body1" style={{ marginTop: "10px" }}>
            The details have been updated successfully.
          </Typography>

          <DialogActions className={classes.dialogActions}>
            <Button
              variant="contained"
              color="primary"
              endIcon={<ChevronRight />}
              style={{
                borderRadius: "20px",
                textTransform: "none",
              }}
              onClick={handleClose}
            >
              Ok
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>
    )}
      <Drawer
        anchor="right"
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
      >
        <Box
          width={410}
          p={3}
          display="flex"
          flexDirection="column"
          height="100%"
        >
          <Typography variant="body1" gutterBottom>
            <b>Warning:</b>
          </Typography>
          <Typography
            variant="body2"
            style={{
              border: "2px solid red", // Red border
              borderRadius: "4px",
              padding: "8px",
              fontWeight: "bold",
              color: "red", // Red text
              lineHeight: 1.5,
            }}
          >
            Permission Change: You are Changing the permissions to default. Team
            members may gain access to few areas of the application. You can't
            reverse this process.
          </Typography>
          <Box display="flex" justifyContent="space-between" mt="auto">
            <Button
              variant="contained"
              color="secondary"
              onClick={() => setIsDrawerOpen(false)}
              style={{
                backgroundColor: "rgba(238, 233, 233, 0.94)",
                color: "black",
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleConfirmReset}
              style={{
                backgroundColor: "#286acf",
                color: "#fff",
              }}
            >
              Confirm changes
            </Button>
          </Box>
        </Box>
      </Drawer>
    </>
  );
}
